import { useCallback, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { STORAGE_KEYS } from '@/shared/constants';

interface IDraftPromptSlot {
  /** Identifies the composer (Rain thread id, or automation id) that owns the draft. */
  scopeId: string;
  message: string;
}

/**
 * Persists a single unsent editor draft prompt at a time.
 *
 * Only ONE draft is kept in localStorage across the whole app: the last
 * composer the user typed in. `scopeId` identifies that composer. Opening a
 * different composer reads the slot, sees a different `scopeId`, and shows
 * nothing; typing there overwrites the slot and evicts the previous draft. So
 * navigating A → B → A never resurfaces A's earlier text once B was edited.
 *
 * Drop-in replacement for `useState<string>('')`. Pass `undefined` to keep the
 * value in memory only — for composers that should not persist (e.g. the
 * run-page surfaces that reuse the editor's chat view).
 *
 * @param {string | undefined} scopeId - Composer id, or `undefined` for in-memory only
 * @param preserveEmpty Retain an explicitly cleared draft so an offered default does not return on remount.
 * @returns The current value, its setter and whether this scope has a saved draft.
 */
export function usePersistedDraftPrompt(
  scopeId: string | undefined,
  preserveEmpty = false,
): readonly [string, (next: string, keepEmpty?: boolean) => void, boolean] {
  const [slot, setSlot] = useLocalStorage<IDraftPromptSlot | null>(
    STORAGE_KEYS.DRAFTS_EDITOR_PROMPT,
    null,
  );
  const [memory, setMemory] = useState('');

  const setScopedValue = useCallback(
    (next: string, keepEmpty = preserveEmpty) => {
      // Writing overwrites the single slot (evicting any other draft); clearing
      // on send removes it.
      setSlot(
        next || keepEmpty ? { scopeId: scopeId ?? '', message: next } : null,
      );
    },
    [scopeId, setSlot, preserveEmpty],
  );

  if (!scopeId) {
    return [memory, setMemory, !!memory];
  }
  // Only surface the stored draft when it belongs to this composer.
  const value = slot?.scopeId === scopeId ? slot.message : '';
  return [value, setScopedValue, slot?.scopeId === scopeId];
}
